export const TERMS_TEXT = `Effective Date: October 10, 2026
Version: Version 1.0

1. Acceptance of Terms
These Terms & Conditions govern the access and use of OJTern, an OJT Finder and coordination platform developed to support the On-the-Job Training (OJT), internship, placement, coordination, and monitoring activities of Dominican College of Tarlac, Inc. (DCT).
For purposes of these Terms & Conditions, "Users" shall refer to the following:
- DCT OJT Students;
- DCT OJT Coordinators; and
- Companies, Organizations, Corporations, Agencies, and other OJT Partners.
By accessing or using OJTern, all Users acknowledge that they have read, understood, and agreed to comply with these Terms & Conditions, the Privacy Policy, applicable DCT policies, OJT requirements, and other applicable rules governing the use of the platform.
OJTern shall only be used for legitimate OJT, internship, educational, placement, coordination, monitoring, administrative, and other authorized institutional purposes.

2. User Accounts and Account Creation
2.1 DCT OJT Coordinators / System Administrators
DCT OJT Coordinator accounts are provided and created by the designated OJTern Head or System Administrator.
OJT Coordinators are authorized administrative Users because they are responsible for handling and managing OJT-related activities within OJTern.
Depending on their assigned responsibilities and authorization level, OJT Coordinators may be permitted to:
- Create and manage DCT OJT Student accounts;
- Manage Student information;
- Review and manage OJT applications;
- Verify and manage Companies and OJT Partners;
- Monitor Student OJT placements;
- Communicate with Students and Companies;
- Maintain OJT-related records and reports; and
- Perform other authorized administrative functions.
Coordinator accounts shall only be used by the designated Coordinator. The OJTern Head or System Administrator may create, activate, modify, restrict, suspend, deactivate, or terminate Coordinator accounts when necessary for legitimate administrative, security, institutional, or operational purposes.

2.2 DCT OJT Student Accounts
DCT OJT Student accounts are created by their respective DCT OJT Coordinators assigned to administer or manage the Student's OJT activities.
Students shall receive the necessary login or access information from their respective Coordinator.
Students are responsible for:
- Keeping their account credentials confidential;
- Providing accurate and updated information;
- Using their account only for authorized OJT-related purposes;
- Not allowing unauthorized individuals to use their account; and
- Immediately reporting suspected unauthorized access or account compromise to their respective Coordinator or authorized OJTern administrator.

2.3 Company, Organization, Corporation, Agency, and OJT Partner Accounts
Companies, Organizations, Corporations, Agencies, and other OJT Partners shall create their own accounts through the OJTern self-registration process.
During registration, they may be required to provide information including:
- Company or Organization Name;
- Industry;
- Business or Organization Location;
- Authorized contact information;
- Account credentials; and
- Appropriate registration, verification, or supporting documents.
Self-registration does not automatically constitute approval or verification. Company and OJT Partner accounts may be subject to review and verification by authorized DCT OJT Coordinators.

3. Company and OJT Partner Registration and Verification
Companies, Organizations, Corporations, Agencies, and other OJT Partners may be required to submit valid documents to establish the legitimacy of their organization.
Depending on the organization, documents may include:
- Department of Trade and Industry (DTI) registration;
- Securities and Exchange Commission (SEC) registration;
- Cooperative Development Authority (CDA) registration;
- Bureau of Internal Revenue (BIR) documents;
- Business or Mayor's Permit;
- Relevant licenses or permits; and
- Other appropriate government-issued registration or supporting documents.

3.1 Restricted Access to Verification Documents
Company and OJT Partner verification documents shall only be accessible to the respective DCT OJT Coordinator(s) who are authorized and assigned to verify or manage the relevant Company or OJT Partner.
Verification documents shall not be accessible to Students, unauthorized Coordinators, other Companies or OJT Partners, or other Users unless such access is specifically authorized for a legitimate institutional, OJT, legal, compliance, or security-related purpose.
This restricted-access arrangement is intended to maintain the security, confidentiality, and proper handling of Company and OJT Partner verification documents.
Authorized Coordinators shall only access verification documents when necessary for legitimate verification, OJT administration, compliance, security, or other authorized institutional purposes.
Authorized Coordinators shall not:
- Publicly display verification documents;
- Share verification documents with unauthorized persons;
- Copy or download verification documents unnecessarily;
- Use verification documents for personal purposes;
- Use verification documents for commercial purposes; or
- Disclose verification documents outside their authorized responsibilities.

3.2 Verification Status
After review, a Company or OJT Partner may receive one of the following statuses:
- Approved — the submitted information and documents have satisfactorily met the verification requirements;
- Declined — the submitted information or documents do not meet the applicable requirements; or
- For Revision / Additional Documents Required — additional information or documentation is required before verification can be completed.
False, fraudulent, altered, expired, or invalid documents may result in rejection, restriction, suspension, or termination of the Company or OJT Partner account and may be referred to appropriate authorities where legally permitted.

4. Use of the Platform
4.1 DCT OJT Students
Students may use OJTern to:
- Search for and view legitimate OJT opportunities;
- Submit OJT applications;
- Communicate with authorized Coordinators and Companies;
- Submit required OJT-related information and documents; and
- Monitor their applications and placement status.
Students shall use OJTern responsibly and in accordance with DCT's OJT requirements and policies.

4.2 DCT OJT Coordinators / Administrators
Coordinators may use OJTern to:
- Create and manage Student accounts;
- Manage Student information;
- Review and manage OJT applications;
- Verify Companies and OJT Partners;
- Access Company verification documents when they are the respective authorized Coordinator(s) assigned to the relevant Company or OJT Partner;
- Monitor OJT placements;
- Communicate with Students and Companies;
- Maintain OJT records and reports; and
- Perform other authorized administrative functions.
Coordinators shall only access personal or confidential information that is necessary for their assigned duties.

4.3 Companies and OJT Partners
Companies and OJT Partners may use OJTern to:
- Create and manage their own accounts;
- Post legitimate OJT opportunities;
- Communicate with Students and Coordinators;
- Review applications submitted to their organization;
- Manage appropriate OJT placement activities; and
- Provide accurate information regarding their organization and available opportunities.
Companies and OJT Partners shall maintain accurate information, provide legitimate OJT opportunities, comply with applicable DCT OJT requirements, maintain a professional environment, treat Students fairly, and protect Student information made available to them.

5. Prohibited Activities
Users shall not:
- Provide false, misleading, fraudulent, altered, or unauthorized information or documents;
- Impersonate another person, Student, Coordinator, Company, or organization;
- Share or misuse another User's account;
- Access another User's account without authorization;
- Harass, threaten, exploit, discriminate against, or improperly treat another User;
- Unauthorizedly collect, use, copy, disclose, sell, distribute, or otherwise process personal information;
- Create or publish false, fraudulent, misleading, or unrelated OJT opportunities;
- Attempt to bypass Company or OJT Partner verification procedures;
- Access Company verification documents without authorization;
- Share confidential Company verification documents with unauthorized persons;
- Misuse administrative privileges;
- Attempt to compromise or interfere with OJTern's security or operation;
- Use personal or confidential information for personal, commercial, or unrelated purposes; or
- Use OJTern for unlawful, fraudulent, or unauthorized activities.
Violations may result in account restriction, suspension, deactivation, termination, or other appropriate administrative or legal action.

6. Account Responsibility and Security
Users are responsible for maintaining the confidentiality and security of their account credentials.
Users shall:
- Keep passwords and authentication information confidential;
- Avoid unauthorized sharing of accounts;
- Use reasonable security practices;
- Change their password when compromise is suspected; and
- Report suspected unauthorized access or security incidents to the appropriate OJTern administrator.
OJT Coordinators shall exercise additional care because their accounts may provide access to administrative functions and confidential OJT information.
In particular, Company verification documents shall only be accessible by the respective authorized Coordinator(s) responsible for the relevant Company or OJT Partner.
The OJTern Head or System Administrator may modify, restrict, suspend, deactivate, or terminate accounts when necessary due to security concerns, changes in assignment, misuse, violation of these Terms, or other legitimate administrative reasons.

7. User Responsibilities
7.1 Students
Students are responsible for:
- Complying with DCT OJT requirements;
- Providing accurate information;
- Properly using their assigned account;
- Maintaining confidentiality of their credentials;
- Protecting information received through OJTern; and
- Maintaining appropriate and professional conduct.

7.2 OJT Coordinators
OJT Coordinators are responsible for:
- Properly administering OJT activities;
- Creating and managing Student accounts;
- Coordinating Student and Company activities;
- Verifying Companies and OJT Partners;
- Protecting Student and Company information;
- Restricting access to verification documents to authorized personnel;
- Using administrative privileges responsibly; and
- Complying with applicable privacy and institutional requirements.

7.3 Companies and OJT Partners
Companies and OJT Partners are responsible for:
- Creating their own accounts;
- Providing accurate and legitimate information;
- Completing required verification procedures;
- Providing legitimate OJT opportunities;
- Protecting Student information;
- Maintaining appropriate professional standards; and
- Complying with applicable OJT requirements and DCT policies.

8. Personal Information and Confidential Information
Users acknowledge that OJTern may process personal information and confidential information necessary for legitimate OJT and administrative purposes.
Users shall not sell, disclose, distribute, copy, misuse, or otherwise process personal or confidential information obtained through OJTern for unauthorized purposes.
Company verification documents shall be treated as confidential and shall only be accessible to the respective authorized Coordinator(s) responsible for Company or OJT Partner verification or management.
All Users and authorized personnel shall comply with the OJTern Privacy Policy and applicable Philippine data privacy laws.

9. Third-Party Services and Data Processing
OJTern may use authorized third-party service providers for necessary technical or operational functions, including:
- Cloud storage;
- Authentication;
- Hosting;
- Database services;
- System maintenance;
- Security services;
- Communication services; and
- Other services necessary for the operation and maintenance of OJTern.
Third-party service providers shall only process personal information for authorized and legitimate purposes and shall be subject to appropriate privacy, confidentiality, and security safeguards.
OJTern shall not authorize third parties to sell, rent, trade, or commercially exploit Users' personal information.
Where personal information is transferred to a third party for processing, appropriate contractual or other reasonable safeguards shall be used, consistent with applicable data privacy requirements.

10. Prohibition on Sale or Unauthorized Commercial Use of Personal Information
OJTern shall not sell, rent, trade, or commercially exploit the personal information of DCT OJT Students, DCT OJT Coordinators, Companies, Organizations, Corporations, Agencies, or other OJT Partners.
Users, OJTern personnel, and authorized third-party processors are prohibited from obtaining personal information through OJTern and subsequently:
- Selling it to another person or organization;
- Providing it to a third party for unauthorized commercial purposes;
- Using it for unauthorized advertising or marketing;
- Trading it for other information or benefits;
- Distributing it for personal gain; or
- Otherwise commercially exploiting it without lawful authority.
Any processing, sharing, or disclosure of personal information must have a legitimate and lawful purpose and must comply with Republic Act No. 10173, otherwise known as the Data Privacy Act of 2012, its Implementing Rules and Regulations, and applicable National Privacy Commission (NPC) issuances.

11. Changes to These Terms
OJTern may update these Terms & Conditions when necessary due to changes in system functionality, institutional policies, applicable laws, regulations, privacy requirements, or operational procedures.
Users may be notified of significant changes through appropriate means.
Continued use of OJTern after the effective date of revised Terms may constitute acceptance of the updated Terms, subject to applicable requirements.

12. Contact
For privacy concerns, questions, requests, or complaints regarding the processing of personal information through OJTern, Users may contact:
Email: ojtern@gmail.com
School Address: Dominican College of Tarlac, Inc., College of Computer Studies, McArthur Highway, Poblacion (Sto. Rosario), Capas, Tarlac, Philippines`;

export const PRIVACY_TEXT = `Effective Date: October 10, 2026
Version: Version 1.0

1. Introduction
OJTern respects the privacy and security of its Users and is committed to protecting personal information and confidential information processed through the platform.
This Privacy Policy explains how OJTern collects, uses, stores, protects, retains, shares, discloses, and otherwise processes information relating to:
- DCT OJT Students;
- DCT OJT Coordinators; and
- Companies, Organizations, Corporations, Agencies, and other OJT Partners.
OJTern shall process personal information in accordance with Republic Act No. 10173, otherwise known as the Data Privacy Act of 2012, its Implementing Rules and Regulations, applicable National Privacy Commission issuances, and relevant DCT institutional policies.

2. Account Creation and Personal Information Processing
2.1 DCT OJT Coordinators / System Administrators
DCT OJT Coordinator accounts are provided and created by the designated OJTern Head or System Administrator.
Coordinators are authorized administrative Users because they handle and manage OJT-related activities.
Information relating to Coordinators may be processed for:
- Account creation and authentication;
- Identity verification;
- Student account administration;
- Company and OJT Partner management;
- OJT monitoring and coordination;
- Communication;
- Recordkeeping;
- System security;
- Administrative accountability; and
- Other legitimate institutional purposes.

2.2 DCT OJT Students
DCT OJT Student accounts are created by their respective DCT OJT Coordinators.
Student information may be processed for:
- Account creation and authentication;
- Student identification;
- OJT applications and placement;
- Communication with Coordinators and Companies;
- Submission and processing of OJT-related documents;
- OJT monitoring;
- Recordkeeping; and
- Other legitimate educational and OJT-related purposes.

2.3 Companies, Organizations, Corporations, Agencies, and OJT Partners
Companies and OJT Partners create their own accounts through the OJTern self-registration process.
Their information may be processed for:
- Account creation and authentication;
- Organization identification;
- Communication;
- Industry and location information;
- Company verification;
- Evaluation and validation;
- Management of legitimate OJT opportunities; and
- Other legitimate OJT-related purposes.
Self-registration does not automatically constitute approval or verification.

3. Information We Collect
3.1 Student Information
OJTern may collect information necessary for OJT-related activities, including:
- Full name;
- Student number;
- Program or course;
- Department or College;
- Contact information;
- OJT information;
- Application information;
- OJT-related documents;
- Placement information;
- Account and authentication information; and
- Other information reasonably necessary for legitimate OJT administration.

3.2 Coordinator Information
OJTern may collect:
- Full name;
- School or institutional identification information;
- Department or assigned OJT responsibility;
- Contact information;
- Account and authentication information;
- OJT administrative activities and records; and
- Other information necessary for legitimate administrative functions.

3.3 Company and OJT Partner Information
OJTern may collect:
- Company or Organization Name;
- Industry;
- Business or Organization Location;
- Authorized contact information;
- OJT opportunity information;
- Account and authentication information;
- Registration and verification documents; and
- Other information necessary for Company verification and legitimate OJT activities.

4. Company Verification Documents
Companies and OJT Partners may be required to submit registration documents, permits, licenses, or other supporting documentation to establish the legitimacy of their organization.
These may include:
- DTI registration;
- SEC registration;
- CDA registration;
- BIR documents;
- Business or Mayor's Permit;
- Relevant licenses or permits; and
- Other appropriate government-issued documents.

4.1 Restricted Access to Verification Documents
Company and OJT Partner verification documents shall only be accessible to the respective DCT OJT Coordinator(s) who are authorized and assigned to verify or manage the relevant Company or OJT Partner.
Students, unauthorized Coordinators, other Companies or OJT Partners, and unrelated Users shall not be permitted to access these documents.
Access may only be granted to another authorized person when such access is necessary for a legitimate institutional, OJT, legal, compliance, security, or other lawful purpose.
Authorized Coordinators shall only access such documents when necessary and shall not unnecessarily copy, download, disclose, distribute, or use them for unrelated purposes.

5. Purpose of Collection and Processing
OJTern may collect and process information for the following purposes:
- Account creation and management;
- Authentication and identification;
- Creation and administration of Student accounts by authorized Coordinators;
- OJT applications and placement;
- Communication among authorized Users;
- Company and OJT Partner verification;
- Monitoring and administration of OJT activities;
- Maintenance of institutional records;
- System security and accountability;
- Compliance with applicable laws and institutional policies; and
- Other legitimate and necessary OJT-related purposes.
Personal information shall not be collected or processed beyond what is reasonably necessary for legitimate purposes.

6. Legal Basis and Compliance with Republic Act No. 10173
OJTern is committed to protecting personal information in accordance with Republic Act No. 10173, otherwise known as the Data Privacy Act of 2012, its Implementing Rules and Regulations, and applicable issuances of the National Privacy Commission.
RA 10173 requires personal information processing to observe the principles of transparency, legitimate purpose, and proportionality. Lawful processing must also be based on an applicable legal basis recognized by the Act.

7. Data Privacy Principles
7.1 Transparency
Users shall be informed about how their personal information is collected, processed, stored, shared, and protected.
7.2 Legitimate Purpose
Personal information shall only be processed for lawful, specified, and legitimate purposes.
7.3 Proportionality
Personal information collected and processed shall be adequate, relevant, and not excessive in relation to the purposes for which it is processed.
7.4 Security
Reasonable and appropriate organizational, physical, and technical measures shall be implemented to protect personal information against unauthorized access, disclosure, alteration, loss, destruction, or other unauthorized processing.

8. Access to Personal and Confidential Information
Access to information shall be controlled according to the User's role, responsibilities, and authorization.
8.1 Students
Students may access their own information and information necessary for their OJT activities.
8.2 OJT Coordinators
Coordinators may access information necessary for their assigned OJT responsibilities. However, access to Company verification documents shall remain restricted to the respective authorized Coordinator(s) responsible for the relevant Company or OJT Partner.
8.3 Companies and OJT Partners
Companies may access information necessary for legitimate OJT activities, including information associated with Students who apply to or are placed with the Company. Companies shall not have access to verification documents belonging to other Companies or OJT Partners.

9. Confidentiality
All Users and authorized personnel who obtain access to personal or confidential information shall maintain its confidentiality.
Information shall not be disclosed, copied, distributed, sold, or used for purposes unrelated to legitimate OJT, educational, administrative, legal, security, or other authorized purposes.
OJT Coordinators have an additional responsibility to protect Company verification documents against unauthorized access, viewing, downloading, copying, disclosure, distribution, modification, or use.

10. Security Measures
OJTern shall implement reasonable and appropriate organizational, physical, and technical security measures designed to protect personal information, including:
- Role-based access controls;
- Authentication mechanisms;
- Restricted administrative access;
- Confidentiality requirements;
- Secure storage mechanisms;
- Monitoring of authorized system activities;
- Security controls for third-party services; and
- Other appropriate safeguards.

11. Third-Party Service Providers and Processing
OJTern may engage authorized third-party service providers to support the operation of the platform, including providers for:
- Cloud storage;
- Hosting;
- Authentication;
- Database management;
- System maintenance;
- Security;
- Communication; and
- Other necessary technical services.
Under Section 21 of RA 10173, a personal information controller remains accountable for personal information under its control or custody, including information transferred to a third party for processing.

12. Prohibition on Selling or Commercial Exploitation of Personal Information
OJTern shall not sell, rent, trade, or commercially exploit the personal information of its Users.
OJTern shall not provide personal information to third parties for the purpose of selling, renting, trading, or commercially exploiting such information.
Unauthorized processing and unauthorized disclosure of personal information may be subject to applicable penalties and liabilities under Republic Act No. 10173.

13. Sharing and Disclosure of Information
Personal information may only be shared or disclosed when reasonably necessary and legally permitted, including with:
- Authorized DCT personnel;
- DCT OJT Coordinators performing assigned OJT duties;
- Students in relation to their own OJT activities;
- Authorized Companies and OJT Partners when necessary for legitimate OJT purposes;
- Authorized technical or service providers;
- Government agencies or regulators when legally required; and
- Other authorized parties when permitted by applicable law.
Company verification documents shall not be routinely shared with Students, unauthorized Coordinators, other Companies, or unrelated third parties.

14. Data Retention and Disposal
Personal information shall be retained only for as long as reasonably necessary to fulfill the legitimate purposes for which it was collected, comply with applicable legal or institutional requirements, maintain necessary OJT records, or establish, exercise, or defend legal claims where applicable.
When information is no longer required, it shall be securely deleted, disposed of, or otherwise rendered inaccessible.

15. Security Incidents and Data Breaches
OJTern shall take reasonable steps to detect, investigate, contain, mitigate, and address suspected security incidents involving personal or confidential information.
Users and personnel who become aware of unauthorized access, disclosure, loss, or other security incidents shall promptly report the incident to the appropriate OJTern administrator.

16. Rights of Data Subjects
Subject to applicable laws and regulations, Data Subjects may have rights concerning their personal information, including:
- The right to be informed;
- The right to access;
- The right to dispute inaccurate or erroneous information;
- The right to object, where applicable;
- The right to request blocking, removal, or destruction where applicable;
- The right to data portability, where applicable; and
- The right to lodge a complaint before the National Privacy Commission, subject to applicable procedures and requirements.

17. Responsibilities of Users Regarding Personal Information
Users shall not:
- Access personal information without authorization;
- Access Company verification documents without authorization;
- Copy or download confidential information unnecessarily;
- Share personal information with unauthorized persons;
- Sell, rent, trade, or commercially exploit personal information;
- Use personal information for unrelated or unauthorized purposes;
- Disclose Company verification documents to third parties without authorization;
- Attempt to bypass system access controls; or
- Otherwise process personal information in violation of applicable laws, these Terms & Conditions, or the OJTern Privacy Policy.

18. No Sale of Personal Information
OJTern maintains a strict policy against the sale or unauthorized commercial exploitation of personal information.
OJTern shall not sell personal information to advertisers, data brokers, Companies, Organizations, Corporations, Agencies, or other third parties.

19. Updates to This Privacy Policy
OJTern may update this Privacy Policy when necessary to reflect changes in OJTern's features, data processing practices, institutional policies, applicable laws and regulations, privacy requirements, or security practices.

20. Governing Privacy Law
This Privacy Policy shall be interpreted and implemented in accordance with applicable laws and regulations of the Republic of the Philippines, including Republic Act No. 10173, otherwise known as the Data Privacy Act of 2012, its Implementing Rules and Regulations, and applicable issuances of the National Privacy Commission.

21. Contact
For privacy concerns, questions, requests, or complaints regarding the processing of personal information through OJTern, Users may contact:
Email: ojtern@gmail.com
School Address: Dominican College of Tarlac, Inc., College of Computer Studies, McArthur Highway, Poblacion (Sto. Rosario), Capas, Tarlac, Philippines`;

// Turns the plain text above into renderable blocks: meta / h2 / h3 / p / ul
export function parseLegalDoc(raw) {
  const lines = raw.split("\n").map((l) => l.trim());
  const blocks = [];
  for (const line of lines) {
    if (!line) continue;
    if (/^_{5,}$/.test(line)) continue;
    if (/^•\s*/.test(line)) {
      const text = line.replace(/^•\s*/, "");
      const last = blocks[blocks.length - 1];
      if (last && last.type === "ul") last.items.push(text);
      else blocks.push({ type: "ul", items: [text] });
      continue;
    }
    if (/^\d+\.\d+\s+\S/.test(line)) { blocks.push({ type: "h3", text: line }); continue; }
    if (/^\d+\.\s+\S/.test(line))     { blocks.push({ type: "h2", text: line }); continue; }
    if (/^(Effective Date|Version):/.test(line)) { blocks.push({ type: "meta", text: line }); continue; }
    blocks.push({ type: "p", text: line });
  }
  return blocks;
}